import React, { useState, useEffect, useCallback } from 'react';
import styles from './ActionTypePickerModal.module.scss';
import { ActionTypePickerResponse, ActionGetResponse } from '../../../api/Api';
import { Icon } from '../../../assets/icons/Icon';
import { useApiClient } from '../../../api/ApiClientContext';

interface ActionTypePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  habitId: string;
  habitName: string;
  actionTypes: ActionTypePickerResponse;
  onSelectActionType: (actionTypeId: string, actionData: ActionGetResponse) => void;
}

export const ActionTypePickerModal: React.FC<ActionTypePickerModalProps> = ({
  isOpen,
  onClose,
  habitId,
  habitName,
  actionTypes,
  onSelectActionType,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const apiClient = useApiClient();
  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setSelectedId(null);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  const handleNext = async () => {
    if (selectedId) {
      setIsLoading(true);
      try {
        const res = await apiClient.api.getHabitActionApiV1HabitsHabitIdActionGet(
          habitId,
          { action_type_id: selectedId }
        );
        console.log('Action data for type:', selectedId, res.data);
        onSelectActionType(selectedId, res.data);
      } catch (error) {
        console.error('Error fetching action:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!isOpen) return null;
  return (
    <div className={styles['action-picker-overlay']} onClick={onClose}>
      <div
        className={styles['action-picker-modal']}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="action-picker-title"
        tabIndex={-1}
      >
        <div className={styles['action-picker-modal__header']}>
          <h2 id="action-picker-title" className={styles['action-picker-modal__title']}>
            Track your habit
          </h2>
          <button
            className={styles['action-picker-modal__close-button']}
            onClick={onClose}
            aria-label="Close modal"
            type="button"
          >
            <Icon iconName="CrossIcon" width="1.2rem" height="1.2rem" />
          </button>
        </div>

        <div className={styles['action-picker-modal__subtitle']}>
          Habit: {habitName} | Date: {formattedDate}
        </div>

        <div className={styles['action-picker-modal__content']}>
          <p className={styles['action-picker-modal__instruction']}>
            Select one of the following options to track your habit:
          </p>

          <div className={styles['action-picker-modal__grid']}>
            {actionTypes.action_types.map((actionType) => (
              <button
                key={actionType.id}
                className={`${styles['action-picker-modal__option']} ${
                  selectedId === actionType.id ? styles['action-picker-modal__option--selected'] : ''
                }`}
                onClick={() => setSelectedId(actionType.id)}
                type="button"
              >
                <div className={styles['action-picker-modal__option-icon']}>
                  {getActionTypeIcon(actionType.name)}
                </div>

                <span className={styles['action-picker-modal__option-name']}>
                  {actionType.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className={styles['action-picker-modal__footer']}>
          <button
            className={styles['action-picker-modal__next-button']}
            onClick={handleNext}
            disabled={!selectedId || isLoading}
            type="button"
          >
            {isLoading ? 'Loading...' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

function getActionTypeIcon(name: string): React.ReactNode {
  const lowerName = name.toLowerCase();
  
  if (lowerName.includes('appreciation')) {
    return <Icon iconName="AppreciationIcon" width="1.66rem" height="1.66rem" />;
  }
  if (lowerName.includes('behaviour reversal') || lowerName.includes('behavior reversal')) {
    return <Icon iconName="BehaviourReversalIcon" width="1.66rem" height="1.66rem" />;
  }
  if (lowerName.includes('daily reflection') || lowerName.includes('reflection')) {
    return <Icon iconName="DailyReflectionIcon" width="1.66rem" height="1.5rem" />;
  }
  if (lowerName.includes('micro-action') || lowerName.includes('micro action') || lowerName.includes('challenge')) {
    return <Icon iconName="MicroActionChallengeIcon" width="1.66rem" height="1.66rem" />;
  }
  if (lowerName.includes('mini-learning') || lowerName.includes('mini learning') || lowerName.includes('learning prompt')) {
    return <Icon iconName="MiniLearningPromptIcon" width="1.66rem" height="1.66rem" />;
  }
  if (lowerName.includes('myth') || lowerName.includes('fact')) {
    return <Icon iconName="MythFactIcon" width="1.66rem" height="1.5rem" />;
  }
  if (lowerName.includes('pattern recognition') || lowerName.includes('pattern')) {
    return <Icon iconName="PatternRecognitionIcon" width="1.66rem" height="1.66rem" />;
  }
  if (lowerName.includes('pulse check') || lowerName.includes('pulse')) {
    return <Icon iconName="PulseCheckIcon" width="1.66rem" height="1.5rem" />;
  }
  if (lowerName.includes('quick quiz') || lowerName.includes('quiz')) {
    return <Icon iconName="QuickQuizIcon" width="1.66rem" height="1.66rem" />;
  }
  if (lowerName.includes('scenario')) {
    return <Icon iconName="ScenarioIcon" width="1.66rem" height="1.66rem" />;
  }
  
  return <Icon iconName="DailyReflectionIcon" width="1.66rem" height="1.5rem" />;
}

